import { DraftSteps, TaskDefinitionFields, DRAFT_STEPS, draftStepLabel } from '@/components/DraftFields'
/**
 * Create or edit a task draft in three steps the expert can move between freely:
 *   1. Task definition  title, program, optional starting task (continuation), the colleague-style request
 *   2. Task brief       the twelve questions about the real work
 *   3. Rubric           what makes a result good (at least two criteria)
 * All fields live in one state object; saving always sends the whole draft, validated against
 * the shared draft schema; completeness is checked when submitting for review. On success the user lands on the task page.
 *
 * Continuation: an expert can declare the draft a continuation of one of their own tasks that has
 * passed the draft review. Choosing one pre-fills brief questions 1-8 from that task (question 4,
 * the starting point, becomes that task's answer to question 1); questions 9-12 stay empty.
 *
 * Routes: /sme/submit (create) and /sme/tasks/:taskId/edit.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { useProfile } from '@/auth/ProfileContext'
import { api, ApiError } from '@/api/client'
import { BriefForm, BriefView, EMPTY_BRIEF, EMPTY_VARIATIONS, editableBrief } from '@/components/BriefForm'
import { ExpertReminder } from '@/components/ExpertReminder'
import { CoachPanel } from '@/components/CoachPanel'
import { Fold } from '@/components/Section'
import { RubricView } from '@/components/RubricView'
import { RubricEditor, blankCriterion, cleanRubric } from '@/components/RubricEditor'
import { validate, type FieldError } from '@/lib/schema'
import { ProgramChips, taskPrograms, programLabel, isScienceExpert } from '@/lib/programs'
import type { Brief, People, RubricCriterion, RubricLayout, Task, TaskDetail } from '@/lib/types'
import { PersonName } from '@/components/PersonName'
import { PromptWithChanges, ReviewChangesSummary, RubricWithChanges, reviewDiff } from '@/components/ReviewChanges'
import { fmtDate } from '@/lib/format'

/** Which step a validation error belongs to, so a failed save can jump to it. */
function stepOf(path: string): number {
  if (path === 'brief' || path.startsWith('brief.')) return 1
  if (path.startsWith('rubric')) return 2
  return 0
}

interface Recovery {
  body: { title: string; program: string; programs?: string[]; prompt: string; brief: Brief; rubric: RubricCriterion[]; rubric_layout?: RubricLayout; parent_task_id?: string | null; revision?: number }
  step: number
}

function readRecovery(key: string): Recovery | null {
  try {
    const copy = JSON.parse(localStorage.getItem(key) ?? 'null') as Recovery | null
    if (!copy || !Number.isInteger(copy.step) || copy.step < 0 || copy.step >= DRAFT_STEPS.length) return null
    const b = copy.body
    // Recovery must also retain text that exceeds the server's length limits.
    return b && ([b.title, b.program, b.prompt].every(v => typeof v === 'string')) &&
      b.brief && Object.keys(EMPTY_BRIEF).every(k => k === 'common_failures'
        ? Array.isArray(b.brief.common_failures) && b.brief.common_failures.every(v => typeof v === 'string')
        : typeof b.brief[k as keyof Brief] === 'string') &&
      Array.isArray(b.rubric) && b.rubric.every(c => c && typeof c.criterion_id === 'string' &&
        typeof c.criterion === 'string' && ['required', 'bonus', 'penalty'].includes(c.role)) &&
      (b.rubric_layout === undefined || (Array.isArray(b.rubric_layout) && b.rubric_layout.every(s => s && typeof s.section_id === 'string' && typeof s.title === 'string' && Array.isArray(s.criterion_ids) && s.criterion_ids.every(id => typeof id === 'string')))) &&
      (b.parent_task_id == null || typeof b.parent_task_id === 'string') &&
      (b.revision === undefined || (Number.isInteger(b.revision) && b.revision >= 0)) ? copy : null
  } catch { return null }
}

export default function SmeSubmit() {
  const { user } = useAuth()
  const { profile } = useProfile()
  const { taskId } = useParams()
  const [params] = useSearchParams()
  if (!user) return null
  // Keep old draft keys recoverable when following a saved request-era URL.
  const scientificCreation = !taskId && (isScienceExpert(profile) || params.get('kind') === 'scientific')
  const key = `seldon.draft.v1:${user.id}:${taskId ?? `new:${scientificCreation ? 'scientific:' : ''}${params.get('request') ?? ''}`}`
  return <DraftEditor key={key} storageKey={key} scientificCreation={scientificCreation} />
}

function DraftEditor({ storageKey, scientificCreation }: { storageKey: string; scientificCreation: boolean }) {
  const { profile } = useProfile()
  const { taskId } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const editing = Boolean(taskId)

  const [recovery] = useState(() => readRecovery(storageKey))
  const saved = useRef(false)
  const [recoveryAvailable, setRecoveryAvailable] = useState(true)
  const [latest, setLatest] = useState<Task | null>(null)
  const [step, setStep] = useState(params.get('section') === 'rubric' ? 2 : recovery?.step ?? 0)
  useEffect(() => {
    const section = params.get('section')
    if (section === 'brief') setStep(1)
    if (section === 'rubric') setStep(2)
    if (section === 'definition') setStep(0)
  }, [params])
  const [loaded, setLoaded] = useState(!editing)
  const [task, setTask] = useState<Task | null>(null)
  const [people, setPeople] = useState<People>({})
  const [title, setTitle] = useState(recovery?.body.title ?? '')
  const [programs, setPrograms] = useState<string[]>(recovery ? taskPrograms(recovery.body) : scientificCreation ? ['Physics'] : [])
  const program = programs.length === 1 ? programs[0] : ''
  const [prompt, setPrompt] = useState(recovery?.body.prompt ?? '')
  const [rubric, setRubric] = useState<RubricCriterion[]>(recovery?.body.rubric ?? [blankCriterion(1), blankCriterion(2)])  // Start with two placeholders; unfinished criteria can be saved.
  const [layout, setLayout] = useState<RubricLayout | undefined>(recovery?.body.rubric_layout)
  const [brief, setBrief] = useState<Brief>(recovery?.body.brief ?? { ...EMPTY_BRIEF, ...(scientificCreation ? EMPTY_VARIATIONS : {}) })
  const scientific = task?.kind === 'scientific_workflow' || scientificCreation
  const [parentId, setParentId] = useState(recovery?.body.parent_task_id ?? '')
  const [parents, setParents] = useState<Task[]>([])       // the expert's tasks that may serve as starting task
  const [autofilled, setAutofilled] = useState(false)      // brief was pre-filled from the starting task in this session
  const [errors, setErrors] = useState<FieldError[]>([])
  const [serverError, setServerError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!taskId) return
    let cancelled = false
    api.get<TaskDetail>(`/tasks/${taskId}`).then(({ task, people }) => {
      if (cancelled) return
      setPeople(people ?? {})
      // Preset with the current values. After a send-back these are the engineer's reviewed
      // versions; the markers below the fields show what differs from what you submitted.
      setTask(recovery ? { ...task, revision: recovery.body.revision ?? 0 } : task)
      if (recovery) {
        if ((recovery.body.revision ?? 0) !== (task.revision ?? 0)) setLatest(task)
      } else {
        setTitle(task.title); setPrograms(taskPrograms(task)); setPrompt(task.prompt); setRubric(task.rubric); setLayout(task.rubric_layout)
        setParentId(task.parent_task_id ?? '')
        setBrief(editableBrief(task.brief, task.kind === 'scientific_workflow'))
      }
      setLoaded(true)
    }).catch((e) => { if (!cancelled) setServerError((e as Error).message) })
    return () => { cancelled = true }
  }, [taskId, recovery])

  useEffect(() => {
    // Only tasks that passed the draft review qualify as starting task: the continuation builds on the reviewed outcome.
    api.get<{ tasks: Task[] }>('/sme/dashboard').then((d) => setParents(d.tasks.filter((t) => t.lifecycle !== 'retired' && t.stage !== 0 && t.task_id !== taskId))).catch(() => setParents([]))
  }, [taskId])

  /** Pick (or clear) the starting task. Picking one pre-fills brief questions 1-8 from it. */
  async function chooseParent(id: string) {
    setParentId(id)
    if (!id) { setAutofilled(false); return }
    try {
      const { task: base } = await api.get<TaskDetail>(`/tasks/${id}`)
      const b = base.brief
      setBrief((cur) => ({
        ...cur,
        what: b.what, requester: b.requester, use: b.use,
        starting_point: b.what,  // the new task starts where the base task's work ends
        finished_state: b.finished_state, judgment_decisions: b.judgment_decisions, rules: b.rules, implicit_expectations: b.implicit_expectations,
      }))
      setPrograms(p => p.length ? p : taskPrograms(base))
      setAutofilled(true)
    } catch (e) { setServerError((e as Error).message) }
  }

  function goTo(n: number) {
    setStep(Math.max(0, Math.min(DRAFT_STEPS.length - 1, n)))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const draft = { title, program, programs, prompt, brief, rubric, rubric_layout: layout, parent_task_id: parentId || null, revision: task?.revision ?? 0 }
  const dirty = loaded && (editing ? !!task && JSON.stringify(draft) !== JSON.stringify({
    title: task.title, program: task.program ?? '', programs: taskPrograms(task), prompt: task.prompt, brief: editableBrief(task.brief, task.kind === 'scientific_workflow'),
    rubric: task.rubric, rubric_layout: task.rubric_layout, parent_task_id: task.parent_task_id ?? null, revision: task.revision ?? 0,
  }) : Boolean(layout || title || programs.length || prompt || parentId || JSON.stringify(brief) !== JSON.stringify(EMPTY_BRIEF) ||
    JSON.stringify(rubric) !== JSON.stringify([blankCriterion(1), blankCriterion(2)])))
  const serialized = JSON.stringify({ body: draft, step })
  useEffect(() => {
    if (!loaded || saved.current) return
    try {
      if (dirty) localStorage.setItem(storageKey, serialized)
      else localStorage.removeItem(storageKey)
      setRecoveryAvailable(true)
    } catch { setRecoveryAvailable(false) }
  }, [loaded, dirty, serialized, storageKey])
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      if (saved.current) return
      event.preventDefault(); event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  function buildBody() {
    const body: Record<string, unknown> = { ...draft, ...(!editing && scientific ? { kind: 'scientific_workflow' } : {}), title: title || (scientific ? 'Untitled workflow' : 'Untitled task'), rubric: cleanRubric(rubric) }
    if (!editing) {
      delete body.revision
    }
    return body
  }

  async function submit() {
    const body = buildBody()
    const errs = validate(editing ? 'task_update' : 'task_create', body)
    setErrors(errs)
    if (errs.length) { goTo(Math.min(...errs.map((e) => stepOf(e.path)))); return }
    setBusy(true); setServerError(null)
    try {
      const detail = editing
        ? await api.put<TaskDetail>(`/sme/tasks/${taskId}`, body)
        : await api.post<TaskDetail>('/sme/tasks', body)
      saved.current = true
      try { localStorage.removeItem(storageKey) } catch { /* The server save succeeded. */ }
      navigate(`/sme/tasks/${detail.task.task_id}`, { state: { justSaved: true } })
    } catch (e) {
      setServerError(`Draft was not saved. ${(e as Error).message}`)
      if (editing && e instanceof ApiError && e.status === 409) {
        try { setLatest((await api.get<TaskDetail>(`/tasks/${taskId}`)).task) } catch { /* Keep the editor and recovery copy. */ }
      }
    } finally { setBusy(false) }
  }

  const rubricErrors = errors.filter((e) => e.path.startsWith('rubric'))
  const errorSteps = new Set(errors.map((e) => stepOf(e.path)))
  if (!loaded) return serverError ? <div className="note err" role="alert">{serverError}</div> : <p className="muted">Loading…</p>
  if (task?.lifecycle === 'retired') return <div className="narrow"><h1>{scientific ? 'Workflow' : 'Task'} retired</h1><p>A developer must restore this {scientific ? 'workflow' : 'task'} before it can be edited.</p><Link className="btn" to={`/sme/tasks/${taskId}`}>View {scientific ? 'workflow' : 'task'}</Link></div>
  const fb = task?.last_feedback
  const repair = !!task && task.stage === 0 && !!fb && fb.to_stage === 0 && fb.from_role === 'engineer'
  const changes = task ? reviewDiff(task) : null

  return (
    <div className="narrow">
      <Link className="backlink" to={editing ? `/sme/tasks/${taskId}` : '/sme'}>← {editing ? scientific ? 'Back to the workflow' : 'Back to the task' : isScienceExpert(profile) ? 'My workflows' : 'My tasks'}</Link>
      <div className="page-head">
        <div>
          <h1>{repair ? 'Repair the draft' : editing ? 'Edit draft' : scientific ? 'New scientific workflow' : 'New task'}</h1>
          <div className="meta">Describe the {scientific ? 'workflow' : 'task'} and intended result. See the <Link to="/sme/guide">guidelines</Link>.</div>
        </div>
      </div>
      {repair && fb && (
        <div className="note warn" style={{ marginTop: '1rem' }}>
          <div className="who">Sent back by the engineer, <PersonName people={people} id={fb.from_id} email={fb.from_email} />, {fmtDate(fb.at)}</div>
          <div className="prewrap">{fb.message}</div>
        </div>
      )}
      {repair && task?.review_notes && <div className="note info" style={{ marginTop: '.75rem' }}><div className="who">Notes from the engineer’s review</div><div className="prewrap">{task.review_notes}</div></div>}
      {repair && task && <ReviewChangesSummary task={task} />}
      {repair && changes?.any && <p className="muted small" style={{ marginTop: '.75rem' }}>The fields in every step start from the reviewed version. Keep the engineer’s edits, change them, or restore your own wording; saving makes this your new draft.</p>}
      {errors.length > 0 && <div className="note err" style={{ marginTop: '1rem' }}>Please fix the {errors.length} highlighted field{errors.length > 1 ? 's' : ''}. Steps with problems are marked below.</div>}

      {recovery && <p className="note info" role="status">Recovered your unsaved draft from this browser. Click Save to save it to your account.</p>}
      {dirty && <p className="muted small" role="status">{recoveryAvailable ? 'Unsaved changes. A recovery copy is kept in this browser; click Save to save to your account.' : 'Unsaved changes. Browser recovery is unavailable; save before leaving this page.'}</p>}
      {latest && <div className="note warn" role="alert">
        <p>The saved task changed since you started editing. Your edits are kept. Compare the latest saved draft before saving again.</p>
        <Fold title="Latest saved draft"><h3>{latest.title}</h3><p>{programLabel(latest)}</p><p className="prewrap">{latest.prompt}</p><BriefView brief={editableBrief(latest.brief)} /><RubricView rubric={latest.rubric} layout={latest.rubric_layout} /></Fold>
        <button type="button" onClick={() => { setTask(latest); setLatest(null); setServerError(null) }}>I’ve compared the drafts; keep my edits</button>
      </div>}
      <ExpertReminder />
      <fieldset disabled={busy} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
      <DraftSteps scientific={scientific} step={step} onChange={goTo} errors={errorSteps} />

      {step === 0 && (
        <section className="form-section" data-tour="draft-definition">
          <h2>{scientific ? 'Workflow definition' : 'Task definition'}</h2>
          <p className="lead">{scientific ? 'Name the workflow and select its applications.' : 'What the task is and the exact request the AI will receive.'}</p>
          {scientific ? <><label className="field"><span>Workflow title</span><input value={title} onChange={e => setTitle(e.target.value)} maxLength={200} /></label><div className="field"><span className="field-label">Applications needed</span><ProgramChips value={programs} onChange={setPrograms} /></div><p className="muted">Describe the workflow in the brief. You will write an exact prompt for each worked example during execution.</p></> : <TaskDefinitionFields title={title} programs={programs} prompt={prompt} parentId={parentId} parents={parents}
            preferred={profile?.programs} errors={errors} onTitle={setTitle} onPrograms={setPrograms}
            onPrompt={setPrompt} onParent={id => void chooseParent(id)} />}
          {repair && task && changes?.promptChanged && (
            <div className="field">
              <span className="field-label">What the engineer changed in the request</span>
              <em className="hint">Compared with the request you submitted. The text box above already contains the engineer’s version.</em>
              <PromptWithChanges task={task} />
            </div>
          )}
        </section>
      )}

      {step === 1 && (
        <section className="form-section" data-tour="draft-brief">
          <h2>{scientific ? 'Workflow brief' : 'Task brief'}</h2>
          <p className="lead">{scientific ? 'Describe the scientific work and useful variations. The developer will use these answers to build instances.' : 'Twelve short questions about the real work. They help the engineering team understand the task before anything is built.'}</p>
          {autofilled && parentId && <div className="note info" style={{ marginBottom: '1rem' }}><strong>Filled out automatically from the starting task. Please check and edit.</strong> Questions 1 to 8 come from that task, question 4 (the starting point) is its answer to question 1. Questions 9 to 12 are yours to fill in.</div>}
          <BriefForm scientific={scientific} value={brief} onChange={setBrief} errors={errors} />
        </section>
      )}

      {step === 2 && (
        <section className="form-section rubric-form-section" data-tour="draft-rubric">
          <h2>Rubric</h2>
          <p className="lead">
            Describe what a successful result must satisfy.
          </p>
          {repair && task && changes && changes.rubric.length > 0 && (
            <div className="field">
              <span className="field-label">What the engineer changed in the rubric</span>
              <em className="hint">The editor below already contains the engineer’s version.</em>
              <RubricWithChanges task={task} />
            </div>
          )}
          <RubricEditor value={rubric} onChange={setRubric} layout={layout} onLayoutChange={setLayout} reservedIds={task ? [...task.retired_criterion_ids ?? [], ...task.rubric.map(c => c.criterion_id)] : undefined} />
          {rubric.length < 2 && <p className="muted small" style={{ marginTop: '.5rem' }}>{rubric.length === 0 ? 'Add at least two criteria.' : 'Add at least one more criterion.'}</p>}
          {rubricErrors.map((e, i) => <div className="field-error" key={i}>{e.path === 'rubric' ? 'Define at least two rubric criteria.' : `${e.path.replace(/^rubric\.(\d+)\.?/, (_, n) => `${rubric[Number(n)]?.criterion_id ?? Number(n) + 1}: `)}${e.message}`}</div>)}
        </section>
      )}

      {step < DRAFT_STEPS.length - 1 && <div className="row wizard-nav-buttons">
        {step > 0 && <button type="button" onClick={() => goTo(step - 1)}>← {draftStepLabel(step - 1, scientific)}</button>}
        {step < DRAFT_STEPS.length - 1 && <button type="button" className="primary" onClick={() => goTo(step + 1)}>{draftStepLabel(step + 1, scientific)} →</button>}
      </div>}

      <div className="coach-fold" hidden={step === 2}>
        {editing && taskId
          ? <Fold title="Coach"><p className="muted small coach-fold-lead">The coach sees the editor text as it is now, saved or not. Engineers can read the conversation.</p><CoachPanel taskId={taskId} role="sme" compact draft={() => ({ programs, prompt, brief, rubric: cleanRubric(rubric) })} /></Fold>
          : <p className="muted small">Save your draft to start a conversation with the coach.</p>}
      </div>

      {serverError && <div className="note err">{serverError}</div>}
      <div className="form-footer">
        <button className={step === DRAFT_STEPS.length - 1 ? 'primary' : ''} disabled={busy || !!latest} onClick={submit}>{busy ? 'Saving…' : editing ? 'Save changes' : 'Save draft'}</button>
        <span className="muted small">Step {step + 1} of {DRAFT_STEPS.length}. {repair ? `Saving returns to the ${scientific ? 'workflow' : 'task'} page, where you submit the repaired draft for review again.` : editing ? `Saving returns to the ${scientific ? 'workflow' : 'task'} page.` : 'Save at any step, even with unanswered questions. Complete the draft before submitting for review.'}</span>
      </div>
      </fieldset>
    </div>
  )
}
